import React, { useState, useEffect, useContext } from 'react'
import GoBack from '../../components/GoBack'
import { Typography, IconButton, BasicTable, Alert } from 'foundations-library/components'
import { VerbalColorType } from 'foundations-library/types'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { EsimContext, ScreenSizeContext } from 'providers'
import { getPhoneCom } from 'phonecom'
import { formatPhoneNumber } from 'phone-numbers'
import ListSkeleton from './components/ListSkeleton'
import { getStatus } from './../../utils/index'
import { listing as listTexts } from './../../utils/objects'
import { makeStyles } from '@material-ui/core/styles'
import AddButton from './components/AddButton'
import Device from './../components/Device'
import Texts from './../components/Texts'
import NoEsimAssigned from './components/NoEsimAssigned'

const useStyles = makeStyles({
    root: {
        '& *': {
            fontFamily: 'Inter, Montserrat, sans-serif !important'
        },
        margin: 'auto',
        marginBottom: '50px',
        paddingRight: ({ screenSize }) => screenSize.mobile || screenSize.tablet ? '5px' : '48px',
        paddingLeft: ({ screenSize }) => screenSize.mobile || screenSize.tablet ? '5px' : '48px',
        maxWidth: '1050px'
    },
    contentContainer: {
        margin: '50px 10px 0',
        '& .table-container': {
            marginBottom: '20px',
            '& th:first-child': {
                minWidth: '300px',
                '@media (max-width: 600px)': {
                    minWidth: '150px'
                }
            }
        }
    }
})

/**
 *
 */
const Index = ({ myEsims = false }: { myEsims?: boolean }) => {
    const screenSize = useContext(ScreenSizeContext)
    const classes = useStyles({ screenSize })
    const [data, setData] = useState<React.ReactNode[][]>([])
    const history = useHistory()
    const { app, esims, fetchEsims, fetchPlans } = useContext(EsimContext)
    let { path } = useRouteMatch()
    path = path.replace(/\/$/, '')

    useEffect(() => {
        const setEsimsData = async (esims, plans) => {
            const phoneCom = await getPhoneCom()
            const isNxtUser = phoneCom.user_tiered as boolean

            let rows: React.ReactNode[][] = []
            if (esims.length) {
                if (!myEsims) {
                    rows = esims.map(esim => {
                        const [statusText, statusCaption, statusTextColor] = getStatus(esim)
                        const ext = esim?.user?.extension?.number || ''
                        const username = isNxtUser && esim.user?.extension?.number ? `${esim.user.extension.first_name} ${esim.user.extension.last_name}` : `${esim.user?.first_name} ${esim.user?.last_name}`

                        const device = esim.esim ? <Device sim={esim.esim.device_name || `eSIM ${esim.service_id}`} id={`ICCID: ${esim.esim.iccid}`} /> : <Device sim={`eSIM ${esim.service_id}`} id='ICCID: NOT ASSIGNED' />
                        const assignedTo = esim.user ? <Texts primary={username} secondary='' /> : null
                        const number = esim.user ? <Texts primary={formatPhoneNumber(esim.user.did)} secondary={`Ext: ${ext}`} primaryColor={statusTextColor as VerbalColorType} /> : null
                        const status = <Texts primary={statusText} secondary={statusCaption} primaryColor={statusTextColor as VerbalColorType} />
                        const action = esim.esim && <IconButton name='go' variant='icon' color='neutral' onMouseUp={() => history.push(`${path}/${esim.service_id}`)} />

                        return [device, assignedTo, number, status, action]
                    })
                } else {
                    const esimsAssignedToMe = esims.filter(esim => Number(esim.user?.voip_phone_id) === Number(phoneCom.voip_phone_id))
                    rows = esimsAssignedToMe.map(esim => {
                        const [statusText, statusCaption, statusTextColor] = getStatus(esim, true)
                        const esimPlan = plans.find(plan => plan.billing_code === esim?.billing_code)
                        const ext = esim?.user?.extension?.number || ''

                        const device = <Device sim={esim.esim.device_name || `eSIM ${esim.service_id}`} id={`ICCID: ${esim.esim.iccid}`} />
                        const plan = <Texts primary={esimPlan.name} secondary={esim.status === 'activated' ? statusCaption : 'Awaiting activation'} primaryColor={'neutral-900'} />
                        const number = <Texts primary={formatPhoneNumber(esim.user.did)} secondary={`Ext: ${ext}`} primaryColor={statusTextColor as VerbalColorType} />
                        const status = <Texts primary={statusText} secondary={statusCaption} primaryColor={statusTextColor as VerbalColorType} />
                        const action = <IconButton name='go' variant='icon' color='neutral' onMouseUp={() => history.push(`${path}/${esim.service_id}`)} />

                        return [device, plan, number, status, action]
                    })
                }

                setData(rows)
            }
        }

        (async () => {
            const [esims, plans] = await Promise.all([fetchEsims(myEsims), fetchPlans()])

            await setEsimsData(esims, plans)
        })()
    }, [JSON.stringify(esims.esims)])

    const columns = !myEsims ? listTexts.admin : listTexts.user

    console.log('eSIMs:', esims)

    const renderContent = (content: JSX.Element) => (
        <>
            {!myEsims && <GoBack title={app?.title ? app.title : 'eSIMs'} />}
            <div className={classes.root}>
                {content}
            </div>
        </>
    )

    if (esims.isLoading) return renderContent(<ListSkeleton />)
    if (esims.error) return renderContent(<Alert level='error' icon={true}>{esims.error}</Alert>)
    if (!esims.esims.length) return renderContent(<Typography variant='h6'><NoEsimAssigned myEsims={myEsims} /></Typography>)
    if (!data.length) return renderContent(<ListSkeleton />)

    return renderContent(
        <>
            <div className={classes.contentContainer}>
                <BasicTable columns={columns} rows={data} />
                {!myEsims && <AddButton />}
            </div>
        </>
    )
}

export default Index
